import { API_BASE } from '../config/apiBase';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft,
  Users, 
  Truck, 
  Building2, 
  Calculator,
  DollarSign,
  FileText,
  Plus,
  Edit,
  Trash2,
  Phone,
  Mail,
  Clock,
  ExternalLink,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = API_BASE;

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchClient();
  }, [id]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API}/clients/${id}`);
      setClient(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error('Error al cargar el cliente');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${id}`, editData);
      toast.success('Cliente actualizado');
      setShowEditDialog(false);
      fetchClient();
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate('/clients')} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver a Clientes
      </Button>

      {/* Client Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold font-['Manrope']">{client.full_name}</h1>
                <div className="flex items-center gap-4 mt-1 text-muted-foreground">
                  {client.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" /> {client.email}
                    </span>
                  )}
                  {client.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" /> {client.phone}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowEditDialog(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            </div>
          </div>

          <Separator className="my-4" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Estatus Migratorio</p>
              <p className="font-medium">{client.immigration_status || 'No especificado'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">CDL</p>
              <p className="font-medium">{client.has_cdl ? 'Sí' : 'No'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Experiencia</p>
              <p className="font-medium">{client.years_experience || 0} años</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Fecha de Registro</p>
              <p className="font-medium">{formatDate(client.created_at)}</p>
            </div>
          </div>

          {client.notes && (
            <div className="mt-4 p-3 rounded-lg bg-muted">
              <p className="text-sm text-muted-foreground mb-1">Notas</p>
              <p className="text-sm">{client.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Button 
          variant="outline" 
          className="h-auto py-4 flex-col"
          onClick={() => navigate(`/apps/appraisal/new?client=${id}`)}
        >
          <Truck className="w-5 h-5 mb-2 text-blue-600" />
          <span className="text-xs">Nueva Tasación</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 flex-col"
          onClick={() => navigate(`/apps/bank-analysis/new?client=${id}`)}
        >
          <Building2 className="w-5 h-5 mb-2 text-emerald-600" />
          <span className="text-xs">Análisis Bancario</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 flex-col"
          onClick={() => navigate(`/apps/credit-evaluation/new?client=${id}`)}
        >
          <Calculator className="w-5 h-5 mb-2 text-purple-600" />
          <span className="text-xs">Evaluación Crédito</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 flex-col"
          onClick={() => navigate(`/apps/pricing/new?client=${id}`)}
        >
          <DollarSign className="w-5 h-5 mb-2 text-amber-600" />
          <span className="text-xs">Estructurar Deal</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 flex-col"
          onClick={() => navigate(`/apps/summary/new?client=${id}`)}
        >
          <FileText className="w-5 h-5 mb-2 text-red-600" />
          <span className="text-xs">Crear Resumen</span>
        </Button>
      </div>

      {/* Tabs for Related Data */}
      <Tabs defaultValue="appraisals" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="appraisals" className="text-xs sm:text-sm">
            <Truck className="w-4 h-4 mr-1 hidden sm:block" />
            Tasaciones ({client.appraisals?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="bank" className="text-xs sm:text-sm">
            <Building2 className="w-4 h-4 mr-1 hidden sm:block" />
            Bancario ({client.bank_analyses?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="credit" className="text-xs sm:text-sm">
            <Calculator className="w-4 h-4 mr-1 hidden sm:block" />
            Crédito ({client.credit_evaluations?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="deals" className="text-xs sm:text-sm">
            <DollarSign className="w-4 h-4 mr-1 hidden sm:block" />
            Deals ({client.deal_structures?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="summaries" className="text-xs sm:text-sm">
            <FileText className="w-4 h-4 mr-1 hidden sm:block" />
            Resúmenes ({client.credit_summaries?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="appraisals" className="mt-4">
          {client.appraisals?.length > 0 ? (
            <div className="space-y-3">
              {client.appraisals.map((a) => (
                <Card key={a.id} className="cursor-pointer hover:shadow-md" onClick={() => navigate(`/apps/appraisal/${a.id}`)}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', a.status === 'completed' ? 'bg-emerald-100' : 'bg-muted')}>
                        <Truck className={cn('w-5 h-5', a.status === 'completed' ? 'text-emerald-600' : 'text-muted-foreground')} />
                      </div>
                      <div>
                        <p className="font-medium">{a.asset?.year} {a.asset?.make} {a.asset?.model}</p>
                        <p className="text-sm text-muted-foreground">
                          Valor tasado: ${a.appraised_value?.toLocaleString() || 'Pendiente'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={a.status === 'completed' ? 'default' : 'secondary'}>
                        {a.status === 'completed' ? 'Completada' : 'Borrador'}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(a.created_at)}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="py-8">
              <CardContent className="text-center">
                <Truck className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No hay tasaciones para este cliente</p>
                <Button variant="link" onClick={() => navigate(`/apps/appraisal/new?client=${id}`)}>
                  <Plus className="w-4 h-4 mr-1" /> Crear tasación
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="bank" className="mt-4">
          {client.bank_analyses?.length > 0 ? (
            <div className="space-y-3">
              {client.bank_analyses.map((a) => (
                <Card key={a.id} className="cursor-pointer hover:shadow-md" onClick={() => navigate(`/apps/bank-analysis/${a.id}`)}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', a.status === 'completed' ? 'bg-emerald-100' : 'bg-muted')}>
                        <Building2 className={cn('w-5 h-5', a.status === 'completed' ? 'text-emerald-600' : 'text-muted-foreground')} />
                      </div>
                      <div>
                        <p className="font-medium">Análisis Bancario</p>
                        <p className="text-sm text-muted-foreground">
                          Ingreso prom: ${a.avg_monthly_income?.toLocaleString() || 0} • {a.total_months_analyzed || 0} meses
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={a.status === 'completed' ? 'default' : 'secondary'}>
                        {a.status === 'completed' ? 'Completado' : 'Borrador'}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(a.created_at)}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="py-8">
              <CardContent className="text-center">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No hay análisis bancarios para este cliente</p>
                <Button variant="link" onClick={() => navigate(`/apps/bank-analysis/new?client=${id}`)}>
                  <Plus className="w-4 h-4 mr-1" /> Crear análisis
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="credit" className="mt-4">
          {client.credit_evaluations?.length > 0 ? (
            <div className="space-y-3">
              {client.credit_evaluations.map((e) => (
                <Card key={e.id} className="cursor-pointer hover:shadow-md" onClick={() => navigate(`/apps/credit-evaluation/${e.id}`)}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', e.assigned_tier ? 'bg-purple-100' : 'bg-red-100')}>
                        <Calculator className={cn('w-5 h-5', e.assigned_tier ? 'text-purple-600' : 'text-red-600')} />
                      </div>
                      <div>
                        <p className="font-medium">Evaluación de Crédito</p>
                        <p className="text-sm text-muted-foreground">
                          {e.assigned_tier ? `Tier ${e.assigned_tier} asignado` : 'No califica para ningún tier'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={e.assigned_tier ? 'default' : 'destructive'}>
                        {e.assigned_tier ? `Tier ${e.assigned_tier}` : 'Sin Tier'}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(e.created_at)}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="py-8">
              <CardContent className="text-center">
                <Calculator className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No hay evaluaciones de crédito para este cliente</p>
                <Button variant="link" onClick={() => navigate(`/apps/credit-evaluation/new?client=${id}`)}>
                  <Plus className="w-4 h-4 mr-1" /> Crear evaluación
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="deals" className="mt-4">
          {client.deal_structures?.length > 0 ? (
            <div className="space-y-3">
              {client.deal_structures.map((d) => (
                <Card key={d.id} className="cursor-pointer hover:shadow-md" onClick={() => navigate(`/apps/pricing/${d.id}`)}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', d.is_valid ? 'bg-amber-100' : 'bg-red-100')}>
                        <DollarSign className={cn('w-5 h-5', d.is_valid ? 'text-amber-600' : 'text-red-600')} />
                      </div>
                      <div>
                        <p className="font-medium">Deal Structure - {d.tier_name}</p>
                        <p className="text-sm text-muted-foreground">
                          ${d.amount_financed?.toLocaleString()} financiado • {d.term_months} meses • {d.interest_rate}%
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {d.is_valid ? (
                        <Badge variant="default"><CheckCircle className="w-3 h-3 mr-1" /> Válido</Badge>
                      ) : (
                        <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" /> Revisar</Badge>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(d.created_at)}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="py-8">
              <CardContent className="text-center">
                <DollarSign className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No hay estructuras de deal para este cliente</p>
                <Button variant="link" onClick={() => navigate(`/apps/pricing/new?client=${id}`)}>
                  <Plus className="w-4 h-4 mr-1" /> Crear deal
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="summaries" className="mt-4">
          {client.credit_summaries?.length > 0 ? (
            <div className="space-y-3">
              {client.credit_summaries.map((s) => (
                <Card key={s.id} className="cursor-pointer hover:shadow-md" onClick={() => navigate(`/apps/summary/${s.id}`)}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="font-medium">Resumen de Crédito</p>
                        <p className="text-sm text-muted-foreground">
                          Recomendación: {s.recommendation}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">{formatDate(s.created_at)}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="py-8">
              <CardContent className="text-center">
                <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No hay resúmenes de crédito para este cliente</p>
                <Button variant="link" onClick={() => navigate(`/apps/summary/new?client=${id}`)}>
                  <Plus className="w-4 h-4 mr-1" /> Crear resumen
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
            <DialogDescription>Actualiza la información del cliente</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit_full_name">Nombre Completo *</Label>
              <Input
                id="edit_full_name"
                value={editData.full_name || ''}
                onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_email">Email</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={editData.email || ''}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_phone">Teléfono</Label>
                <Input
                  id="edit_phone"
                  value={editData.phone || ''}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Estatus Migratorio</Label>
                <Select 
                  value={editData.immigration_status || ''} 
                  onValueChange={(v) => setEditData({ ...editData, immigration_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="citizen">Ciudadano</SelectItem>
                    <SelectItem value="resident">Residente</SelectItem>
                    <SelectItem value="visa">Visa de Trabajo</SelectItem>
                    <SelectItem value="itin">ITIN</SelectItem>
                    <SelectItem value="other">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_years">Años de Experiencia</Label>
                <Input
                  id="edit_years"
                  type="number"
                  min="0"
                  value={editData.years_experience || 0}
                  onChange={(e) => setEditData({ ...editData, years_experience: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit_cdl"
                checked={editData.has_cdl || false}
                onCheckedChange={(checked) => setEditData({ ...editData, has_cdl: checked })}
              />
              <Label htmlFor="edit_cdl" className="cursor-pointer">Tiene CDL</Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_notes">Notas</Label>
              <Textarea
                id="edit_notes"
                value={editData.notes || ''}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancelar</Button>
            <Button onClick={handleUpdate} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetail;
